import React, { useState } from "react";
import { X } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { PROJECT_TYPES, STATUS_LABELS } from "../lib/constants";
import { useSettings } from "../lib/settings";

/**
 * CreateProjectModal — single-step form for Stage 1. If no clients exist,
 * surfaces the "create client first" helper state.
 */
export default function CreateProjectModal({ clients, onClose, onCreated }) {
  const { settings } = useSettings();
  const projectTypes = settings?.project_types?.length ? settings.project_types : PROJECT_TYPES;
  const [form, setForm] = useState({
    project_reference: "",
    project_name: "",
    client_id: clients?.[0]?.id || "",
    address: "",
    project_type: "",
    summary: "",
    current_status: "pending",
    next_milestone: "",
    start_date: "",
    due_date: "",
    client_uploads_enabled: true,
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.start_date) delete payload.start_date;
      if (!payload.due_date) delete payload.due_date;
      await api.post("/projects", payload);
      onCreated();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="card-surface w-full max-w-2xl p-8 fade-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="create-project-modal"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="label text-[color:var(--atech-copper)] mb-1">New project</div>
            <h2 className="font-display text-2xl text-white tracking-tight">Create a project</h2>
          </div>
          <button onClick={onClose} className="text-[color:var(--atech-text-dim)] hover:text-white" data-testid="close-modal">
            <X size={20} />
          </button>
        </div>

        {!clients?.length ? (
          <div className="text-sm text-[color:var(--atech-text-dim)] border border-white/10 rounded-[2px] p-5">
            You need at least one client organisation first. Go to{" "}
            <span className="text-[color:var(--atech-copper)]">Clients → New client</span>.
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Project reference" required>
                <input
                  required
                  value={form.project_reference}
                  onChange={(e) => update("project_reference", e.target.value)}
                  placeholder="AT-2026-001"
                  className={inputCls}
                  data-testid="project-reference-input"
                />
              </Field>
              <Field label="Client" required>
                <select
                  required
                  value={form.client_id}
                  onChange={(e) => update("client_id", e.target.value)}
                  className={inputCls}
                  data-testid="project-client-select"
                >
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Project name" required>
              <input
                required
                value={form.project_name}
                onChange={(e) => update("project_name", e.target.value)}
                placeholder="Kingsmere Healthcare — Refurbishment"
                className={inputCls}
                data-testid="project-name-input"
              />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Address / location">
                <input
                  value={form.address}
                  onChange={(e) => update("address", e.target.value)}
                  className={inputCls}
                  data-testid="project-address-input"
                />
              </Field>
              <Field label="Project type">
                <select
                  value={form.project_type}
                  onChange={(e) => update("project_type", e.target.value)}
                  className={inputCls}
                  data-testid="project-type-select"
                >
                  <option value="">Select type…</option>
                  {projectTypes.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Initial status">
                <select
                  value={form.current_status}
                  onChange={(e) => update("current_status", e.target.value)}
                  className={inputCls}
                  data-testid="project-status-select"
                >
                  {Object.entries(STATUS_LABELS).filter(([k]) => k !== "on_hold").map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </Field>
              <Field label="Next milestone">
                <input
                  value={form.next_milestone}
                  onChange={(e) => update("next_milestone", e.target.value)}
                  placeholder="Planning determination expected 12 May"
                  className={inputCls}
                />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Start date">
                <input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => update("start_date", e.target.value)}
                  className={inputCls}
                  data-testid="project-start-date-input"
                />
              </Field>
              <Field label="Due date">
                <input
                  type="date"
                  value={form.due_date}
                  onChange={(e) => update("due_date", e.target.value)}
                  className={inputCls}
                  data-testid="project-due-date-input"
                />
              </Field>
            </div>
            <Field label="Summary">
              <textarea
                rows={3}
                value={form.summary}
                onChange={(e) => update("summary", e.target.value)}
                className={`${inputCls} resize-none`}
                data-testid="project-summary-input"
              />
            </Field>

            <label className="flex items-center gap-3 text-sm text-[color:var(--atech-text-dim)] cursor-pointer">
              <input
                type="checkbox"
                checked={form.client_uploads_enabled}
                onChange={(e) => update("client_uploads_enabled", e.target.checked)}
                className="accent-[color:var(--atech-copper)]"
                data-testid="project-client-uploads-toggle"
              />
              Allow the client to upload files into this project
            </label>

            {err && (
              <div className="text-xs text-[#F87171] border border-[rgba(220,38,38,0.25)] bg-[rgba(220,38,38,0.08)] rounded-[2px] px-3 py-2">
                {err}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} className="text-sm text-[color:var(--atech-text-dim)] hover:text-white px-4 py-2.5">
                Cancel
              </button>
              <button type="submit" disabled={busy} className="btn-copper text-sm" data-testid="create-project-submit">
                {busy ? "Creating…" : "Create project"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-[color:var(--atech-surface-elevated)] border border-white/10 rounded-[2px] px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-[color:var(--atech-copper)] focus:ring-1 focus:ring-[color:var(--atech-copper)]";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="label block mb-2">
      {label} {required && <span className="text-[color:var(--atech-copper)]">*</span>}
    </span>
    {children}
  </label>
);
